import React, { useState, useEffect } from "react";
import {
  Drawer,
  Table,
  TableColumnsType,
  Empty,
  Spin,
  Flex,
  Tabs,
  Collapse as AntCollapse,
  Descriptions,
  Popover,
  Modal,
} from "antd";
import { Badge } from "reactstrap";
import { PaperClipOutlined, DownloadOutlined } from "@ant-design/icons";
import mainDashboardApis from "../../api/dashboard/MainDashboard";
import creditDebitNoteApis from "../../api/credit-debit-note/credit-debit-note";
import { formatCurrency } from "../../utils/Utils";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import FormatDate from "../masters/DateFormater";
import axios from "axios";
import Swal from "sweetalert2";

const { TabPane } = Tabs;
const { Panel } = AntCollapse;

interface AccountsPayablesDrawerProps {
  visible: boolean;
  onClose: () => void;
  site: string;
  contractor: string;
  dateRange: { from: string; to: string };
  categoryId?: string;
}

const AccountsPayablesDrawer: React.FC<AccountsPayablesDrawerProps> = ({
  visible,
  onClose,
  site,
  contractor,
  dateRange,
  categoryId,
}) => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [isLoading, setIsLoading] = useState(false);
  const [payableInvoiceData, setPayableInvoiceData] = useState<any[]>([]);
  const [payableInvoicePagination, setPayableInvoicePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [creditBalanceData, setCreditBalanceData] = useState<any[]>([]);
  const [creditBalancePagination, setCreditBalancePagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isCreditBalanceLoading, setIsCreditBalanceLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("payableInvoice");
  const navigate = useNavigate();
  const [creditNoteDrawer, setCreditNoteDrawer] = useState(false);
  const [creditNoteDrawerData, setCreditNoteDrawerData] = useState<any>(null);
  const [billingAddress, setBillingAddress] = useState<any>(null);
  const [instructionsVisible, setInstructionsVisible] = useState(false);
  const [termsVisible, setTermsVisible] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState<any>("");
  const [attachments, setAttachments] = useState<any>([]);
  const [signature, setSignature] = useState<any>([]);
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  const payableInvoiceColumns: TableColumnsType<any> = [
    {
      title: "Invoice #",
      dataIndex: "invoiceId",
      key: "invoiceId",
      width: 200,
      fixed: "left",
      render: (text: any, record: any) => {
        if (!text && !record.invoiceNumber) return <span>-</span>;
        const invoiceId = text || record.invoiceId;
        const invoiceNumber = record.invoiceNumber;
        return (
          <span
            className="inlineTableCellLink"
            style={{ cursor: "pointer", color: "#2263b3" }}
            onClick={() => {
              if (invoiceNumber) {
                navigate(`/view-invoice/${invoiceNumber}`, {
                  state: {
                    fromAccountsPayablesDrawer: true,
                    previousPath: "/dashboard",
                    previousState: {
                      openAccountsPayablesDrawer: true,
                      site,
                      contractor,
                      dateRange,
                      categoryId,
                    },
                  },
                });
              }
            }}
          >
            {invoiceId}
          </span>
        );
      },
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 150,
      render: (date: any) =>
        date ? dayjs(date).format("DD-MMM-YYYY") : "-",
    },
    {
      title: "Supplier",
      dataIndex: "Supplier",
      key: "Supplier",
      width: 200,
    },
    {
      title: "Order #",
      dataIndex: "orderId",
      key: "orderId",
      width: 150,
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      key: "Amount",
      width: 150,
      render: (text: any, record: any) => {
        const amount = text || record.Amount || 0;
        return (
          <div style={{ textAlign: "right" }}>
            <span>{formatCurrency(amount, record.currencyCode || "INR")}</span>
          </div>
        );
      },
    },
    {
      title: "Outstanding Amount",
      dataIndex: "outstandingAmount",
      key: "outstandingAmount",
      width: 170,
      render: (text: any, record: any) => {
        const amount = text || record.outstandingAmount || 0;
        return (
          <div style={{ textAlign: "right" }}>
            <span>{formatCurrency(amount, record.currencyCode || "INR")}</span>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: 120,
    },
    {
      title: "Payment Status",
      dataIndex: "actualStatus",
      key: "actualStatus",
      fixed: "right",
      width: 150,
      align: "left",
      render: (text, record) => {
        const status = text || record.paymentStatus;
        if (!status) return <span>-</span>;
        return (
          <>
            <div className="justify-start align-center">
              <h6
                className={`tb-status ms-1`}
                style={{
                  fontSize: "14px",
                  color:
                    status === "Approved"
                      ? "#28A745" // Green
                      : status === "Open"
                      ? "#007BFF" // Blue
                      : status === "In Progress"
                      ? "#FD7E14" // Orange
                      : status === "Rejected"
                      ? "#DC3545" // Red
                      : status === "Pending"
                      ? "#FFC107" // Yellow
                      : "black", // Default color
                }}
              >
                {status}
              </h6>
            </div>
          </>
        );
      },
    },
    {
      title: "Delivery Days",
      dataIndex: "DeliveryDays",
      key: "DeliveryDays",
      width: 200,
      render: (text: any, record: any) => {
        // Use DeliveryDays from API response if available
        if (record.DeliveryDays) {
          const isOverdue = record.DeliveryDays.toLowerCase().includes("overdue");
          return (
            <span
              style={{
                color: isOverdue ? "#ff4d4f" : "#52c41a",
                fontWeight: isOverdue ? "bold" : "normal",
              }}
            >
              {record.DeliveryDays}
            </span>
          );
        }
        // Fallback if DeliveryDays is not provided
        if (!text) return <span style={{ color: "#666" }}>-</span>;
        const isOverdue = text.toLowerCase().includes("overdue");
        return (
          <span
            style={{
              color: isOverdue ? "#ff4d4f" : "#52c41a",
              fontWeight: isOverdue ? "bold" : "normal",
            }}
          >
            {text}
          </span>
        );
      },
    },
  ];

  const creditBalanceColumns: TableColumnsType<any> = [
    {
      title: "Credit/Debit #",
      dataIndex: "noteId",
      key: "noteId",
      width: 200,
      fixed: "left",
      render: (text: any, record: any) => {
        const noteId = text || record.noteId;
        const noteNumber = record.noteNumber || noteId;
        return (
          <span
            className="inlineTableCellLink"
            style={{ cursor: "pointer", color: "#2263b3" }}
            onClick={() => {
              if (noteNumber || noteId) {
                fetchCreditNoteDetails(noteNumber || noteId);
              }
            }}
          >
            {noteId}
          </span>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 150,
      render: (date: any) =>
        date ? dayjs(date).format("DD-MMM-YYYY") : "-",
    },
    {
      title: "Supplier",
      dataIndex: "supplierName",
      key: "supplierName",
      width: 200,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      render: (text: any) => text || "-",
    },
    {
      title: "Credit Balance",
      dataIndex: "creditBalance",
      key: "creditBalance",
      width: 170,
      render: (text: any, record: any) => {
        const amount = text || record.creditBalance || 0;
        return (
          <div style={{ textAlign: "right" }}>
            <span>{formatCurrency(amount, record.currencyCode || "INR")}</span>
          </div>
        );
      },
    },
  ];

  const fetchPayableInvoices = async (page: number = 1, pageSize: number = 10) => {
    setIsLoading(true);
    try {
      const params: any = {
        page,
        pageSize,
      };

      if (dateRange.from && dateRange.to) {
        params.startDate = dateRange.from;
        params.endDate = dateRange.to;
      }

      if (site) {
        params.siteId = site;
      }

      if (contractor) {
        params.contractorId = contractor;
      }

      if (currentUser?.orgid) {
        params.orgId = currentUser.orgid;
      }

      if (categoryId) {
        params.categoryId = categoryId;
      }

      const res = await mainDashboardApis.payableInvoicesList(params);
      if (res?.status) {
        setPayableInvoiceData(res.response?.data || res.data || []);
        setPayableInvoicePagination({
          current: res.response?.currentPage || res.currentPage || page,
          pageSize: res.response?.pageSize || res.pageSize || pageSize,
          total: res.response?.totalRecords || res.totalRecords || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching payable invoices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCreditBalance = async (page: number = 1, pageSize: number = 10) => {
    setIsCreditBalanceLoading(true);
    try {
      const requestBody: any = {
        page,
        pageSize,
      };

      if (dateRange.from && dateRange.to) {
        requestBody.startDate = dateRange.from;
        requestBody.endDate = dateRange.to;
      }

      if (site) {
        requestBody.siteId = site;
      }

      if (contractor) {
        requestBody.contractorId = contractor;
      }

      if (currentUser?.orgid) {
        requestBody.orgId = currentUser.orgid;
      }

      if (categoryId) {
        requestBody.categoryId = categoryId;
      }

      const res = await mainDashboardApis.payableCreditBalanceList(requestBody);
      if (res?.status) {
        setCreditBalanceData(res.response?.data || res.data || []);
        setCreditBalancePagination({
          current: res.response?.currentPage || res.currentPage || page,
          pageSize: res.response?.pageSize || res.pageSize || pageSize,
          total: res.response?.totalRecords || res.totalRecords || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching credit balance:", error);
    } finally {
      setIsCreditBalanceLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      // Call both APIs when drawer opens, similar to Top Purchase Categories
      fetchPayableInvoices(1, 10);
      fetchCreditBalance(1, 10);
    }
  }, [visible, site, contractor, dateRange, categoryId]);

  const handlePayableInvoiceTableChange = (newPagination: any) => {
    fetchPayableInvoices(newPagination.current, newPagination.pageSize);
  };

  const handleCreditBalanceTableChange = (newPagination: any) => {
    fetchCreditBalance(newPagination.current, newPagination.pageSize);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const fetchCreditNoteDetails = async (id: any) => {
    try {
      const res = await creditDebitNoteApis.fetchCreditDebitNote(id);
      const { data, status, message } = res;
      if (status) {
        setCreditNoteDrawer(true);
        setCreditNoteDrawerData(data);
        const { dms } = data;
        if (dms?.length > 0) {
          const _signature = dms?.find(
            (item: any) => item.moduleFileType == "signature"
          );
          if (_signature) {
            setSignature({
              docId: _signature?.docId,
              fileName: _signature?.fileName,
              image: _signature?.filePath,
              moduleFileType: _signature?.moduleFileType,
              module: _signature?.module,
              label: data?.signatureLabel,
              isActive: true,
            });
          }
          const _termsAndConditions = dms?.find(
            (item: any) => item.moduleFileType == "termsAndConditions"
          );
          if (_termsAndConditions) {
            const data: any = await fetchHtmlContentFromUrl(
              _termsAndConditions?.filePath
            );
            setTermsAndConditions(data);
          }
          const _instructions = dms?.find(
            (item: any) => item.moduleFileType == "instructions"
          );
          if (_instructions) {
            const data: any = await fetchHtmlContentFromUrl(
              _instructions?.filePath
            );
            setInstructions(data);
          }
          const _attachments = dms?.filter(
            (item: any) => item.moduleFileType == "attachments" && item.docHash
          );
          if (_attachments) {
            setAttachments(
              _attachments.map((attachment: any) => ({
                docId: attachment?.docId,
                name: attachment?.fileName,
                link: attachment?.filePath,
                moduleFileType: attachment?.moduleFileType,
                module: attachment?.module,
                label: "attachments",
                isActive: true,
              }))
            );
          }
        }
        setBillingAddress(data?.billingAddress);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.error("Error fetching credit note details:", error);
    }
  };

  const fetchHtmlContentFromUrl = async (url: string) => {
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "text/html",
        },
      });
      return response?.data;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const SupplierAddress = ({ address }: any) => {
    let _address = [
      address?.addressLine1,
      address?.addressLine2,
      address?.city,
      address?.pincode,
      address?.districtInfo?.districtName,
      address?.stateInfo?.stateName,
      address?.countryInfo?.countryName,
    ]
      .filter(Boolean)
      .join(", ");
    return (
      <div>
        <p>{_address || "N/A"}</p>
      </div>
    );
  };

  const drawerColumn: any = [
    {
      title: "Line #",
      dataIndex: "invoiceLineNo",
      key: "invoiceLineNo",
      width: 100,
      align: "center",
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: 150,
      align: "center",
      fixed: "left",
    },
    {
      title: "HSN Code",
      dataIndex: "hsnCode",
      key: "hsnCode",
      width: 100,
      align: "center",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 150,
      align: "center",
      render: (text: any, record: any) => (
        <span>
          {Number(text).toFixed(2)} {record?.uomShortCode}
        </span>
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 150,
      align: "center",
    },
    {
      title: "Net Amount",
      dataIndex: "netAmount",
      key: "netAmount",
      width: 150,
      align: "center",
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 150,
      align: "center",
      render: (date: any) => <span>{FormatDate(date)}</span>,
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      setScreenSize(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Drawer
      title="Accounts Payables (Outstanding)"
      placement="right"
      onClose={onClose}
      open={visible}
      width="85%"
    >
      <Tabs defaultActiveKey="payableInvoice" onChange={handleTabChange}>
        <TabPane tab={<span>Payable Invoice</span>} key="payableInvoice">
          {isLoading ? (
            <Flex align="center" gap="middle" className="pageLoader">
              <Spin size="large" className="m-auto" />
            </Flex>
          ) : payableInvoiceData.length === 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Empty description="No Payable Invoices Found" />
            </div>
          ) : (
            <Table
              className="customTable"
              columns={payableInvoiceColumns}
              dataSource={payableInvoiceData}
              rowKey={(record) => record.invoiceId || record.invoiceNumber || `invoice-${record.orgId}-${record.siteId}-${record.supplierId}`}
              pagination={{
                current: payableInvoicePagination.current,
                pageSize: payableInvoicePagination.pageSize,
                total: payableInvoicePagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              onChange={handlePayableInvoiceTableChange}
              bordered
              size="middle"
              scroll={{ x: 1200 }}
            />
          )}
        </TabPane>
        <TabPane tab={<span>Credit Balance</span>} key="creditBalance">
          {isCreditBalanceLoading ? (
            <Flex align="center" gap="middle" className="pageLoader">
              <Spin size="large" className="m-auto" />
            </Flex>
          ) : creditBalanceData.length === 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Empty description="No Credit Balance Found" />
            </div>
          ) : (
            <Table
              className="customTable"
              columns={creditBalanceColumns}
              dataSource={creditBalanceData}
              rowKey={(record) => record.noteId || `credit-${record.orgId}-${record.supplierId}`}
              pagination={{
                current: creditBalancePagination.current,
                pageSize: creditBalancePagination.pageSize,
                total: creditBalancePagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              onChange={handleCreditBalanceTableChange}
              bordered
              size="middle"
              scroll={{ x: 1200 }}
            />
          )}
        </TabPane>
      </Tabs>

      {/* Credit Note Detail Drawer */}
      <Drawer
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>{creditNoteDrawerData?.noteId}</strong>
          </div>
        }
        placement="right"
        onClose={() => setCreditNoteDrawer(false)}
        open={creditNoteDrawer}
        width="85vw"
        style={{
          position: "absolute",
          zIndex: 1100,
        }}
        styles={{ body: { paddingBottom: 80 } }}
      >
        {creditNoteDrawerData ? (
          <>
            <AntCollapse defaultActiveKey={["1", "2", "3"]}>
              <Panel header="General Information" key="1">
                <div className={`tab-pane active}`}>
                  <div className="justify-start w-100 flex-wrap mt-0 p-0">
                    <div className="w-30 border-end">
                      <div className="data-col align-center pt-1">
                        <div className="data-label w-30">Reference #</div>
                        <div className="data-value w-70">
                          {creditNoteDrawerData?.referenceNumber || "--"}
                        </div>
                      </div>
                      <div className="data-col align-center pt-1">
                        <div className="data-label w-30">Supplier</div>
                        <div className="data-value w-70">
                          {creditNoteDrawerData?.supplierName}
                        </div>
                      </div>

                      <div className="data-col align-center pt-1">
                        <div className="data-label w-30">Site</div>
                        <div className="data-value w-70">
                          {creditNoteDrawerData?.siteName || "N/A"}
                        </div>
                      </div>

                      <div className="data-col align-center pt-1">
                        <div className="data-label w-30">Note Type</div>
                        <div className="data-value w-70">
                          <Badge color="outline-success">
                            {creditNoteDrawerData?.noteType || "--"}
                          </Badge>
                        </div>
                      </div>

                      <div className="data-col align-center pt-1">
                        <div className="data-label w-30">Note Date</div>
                        <div className="data-value w-70">
                          {creditNoteDrawerData?.noteDate
                            ? dayjs(creditNoteDrawerData?.noteDate).format(
                                "DD-MMM-YYYY"
                              )
                            : "--"}
                        </div>
                      </div>
                    </div>
                    <div className="w-70 paddingLeft10">
                      <div className="data-col align-center pt-1">
                        <div className="data-label w-25">Approval Required</div>
                        <div className="data-value w-75">
                          {!creditNoteDrawerData?.autoConfirmed ? (
                            <Badge color="outline-success">Yes</Badge>
                          ) : (
                            <Badge color="outline-danger">No</Badge>
                          )}
                        </div>
                      </div>

                      <div className="data-col align-center pt-1">
                        <div className="data-label w-25">Billing Address</div>
                        <div className="data-value w-75">
                          <Popover
                            content={
                              <div>
                                <p>{creditNoteDrawerData?.billingAddressDetails}</p>
                              </div>
                            }
                            title={
                              <span style={{ color: "#2263b3" }}>
                                Billing Address
                              </span>
                            }
                          >
                            <span
                              style={{
                                color: "#2263b3",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              View
                            </span>
                          </Popover>
                        </div>
                      </div>
                      <div className="data-col align-center pt-1">
                        <div className="data-label w-25">Delivery Address</div>
                        <div className="data-value w-75">
                          <Popover
                            content={
                              <div>
                                <p>{creditNoteDrawerData?.deliveryAddressDetails}</p>
                              </div>
                            }
                            title={
                              <span style={{ color: "#2263b3" }}>
                                Delivery Address
                              </span>
                            }
                          >
                            <span
                              style={{
                                color: "#2263b3",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              View
                            </span>
                          </Popover>
                        </div>
                      </div>

                      <div className="data-col align-center pt-1">
                        <div className="data-label w-25">Supplier Address</div>
                        <div className="data-value w-75">
                          <Popover
                            content={
                              <SupplierAddress
                                address={
                                  creditNoteDrawerData?.supplierDetails?.address
                                }
                              />
                            }
                            title={
                              <span style={{ color: "#2263b3" }}>
                                Supplier Address
                              </span>
                            }
                          >
                            <span
                              style={{
                                color: "#2263b3",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              View
                            </span>
                          </Popover>
                        </div>
                      </div>

                      <div className="data-col align-center pt-1">
                        <div className="data-label w-25">Description</div>
                        <div className="data-value w-75">
                          <Popover
                            content={
                              <div>
                                <p>{creditNoteDrawerData?.description}</p>
                              </div>
                            }
                            title={"Description"}
                          >
                            {creditNoteDrawerData?.description?.length > 100 ? (
                              <span
                                style={{
                                  color: "#2263b3",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                {creditNoteDrawerData?.description?.substring(
                                  0,
                                  100
                                )}
                                ...
                              </span>
                            ) : (
                              creditNoteDrawerData?.description || "--"
                            )}
                          </Popover>
                        </div>
                      </div>
                      <div className="data-col align-center pt-1">
                        <div className="data-label w-25">Remarks</div>
                        <div className="data-value w-75">
                          {creditNoteDrawerData?.remarks || "--"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Panel>
              <Panel
                header={
                  creditNoteDrawerData?.creditDebitNoteLines?.length > 0
                    ? "Material Details"
                    : "Credit Note Details"
                }
                key="2"
              >
                <div className="table-responsive poTable">
                  {creditNoteDrawerData?.creditDebitNoteLines?.length > 0 ? (
                    <div
                      style={{
                        width: screenSize - 300 + "px",
                        margin: "auto",
                      }}
                    >
                      <Table
                        className="customTable"
                        columns={drawerColumn?.map((col: any) => {
                          if (
                            [
                              "unitPrice",
                              "basicAmount",
                              "netAmount",
                              "grossTotal",
                            ].includes(col?.dataIndex)
                          ) {
                            return {
                              ...col,
                              render: (text: any, record: any) => {
                                return (
                                  <div className="justify-end">
                                    {formatCurrency(
                                      Number(text),
                                      creditNoteDrawerData.currencyCode
                                    )}
                                  </div>
                                );
                              },
                            };
                          }
                          if (col?.children?.length > 0) {
                            return {
                              ...col,
                              children: col?.children?.map((_col: any) => {
                                if (_col?.title == "Amount") {
                                  return {
                                    ..._col,
                                    render: (text: any, record: any) => {
                                      return (
                                        <div className="justify-end">
                                          {formatCurrency(
                                            Number(text),
                                            creditNoteDrawerData.currencyCode
                                          )}
                                        </div>
                                      );
                                    },
                                  };
                                }
                                return _col;
                              }),
                            };
                          }
                          if (!col.editable) {
                            return col;
                          }
                          return {
                            ...col,
                            onCell: (record: any) => ({
                              record,
                              inputType:
                                col.dataIndex === "quantity"
                                  ? "number"
                                  : "text",
                              dataIndex: col.dataIndex,
                              title: col.title,
                              editing: true,
                            }),
                          };
                        })}
                        dataSource={creditNoteDrawerData?.creditDebitNoteLines?.map(
                          (field: any) => {
                            return {
                              ...field,
                              key: field.lineNo,
                            };
                          }
                        )}
                        scroll={{ x: "max-content" }}
                        bordered
                        size="small"
                        pagination={false}
                      />
                    </div>
                  ) : (
                    <div className="m-2 mb-0 w-50">
                      <Descriptions
                        bordered
                        column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                        size="small"
                        labelStyle={{ fontWeight: "bold" }}
                        contentStyle={{ fontWeight: "normal" }}
                      >
                        <Descriptions.Item label="Line Name">
                          <strong> Amount</strong>
                        </Descriptions.Item>
                        {creditNoteDrawerData?.jsonLineData?.map(
                          (line: any) => {
                            return (
                              <>
                                <Descriptions.Item label={line?.name}>
                                  {formatCurrency(
                                    Number(line?.noteAmount),
                                    creditNoteDrawerData.currencyCode
                                  )}
                                </Descriptions.Item>
                              </>
                            );
                          }
                        )}
                        <Descriptions.Item label="Total Amount">
                          <strong>
                            {formatCurrency(
                              Number(creditNoteDrawerData?.noteAmount),
                              creditNoteDrawerData.currencyCode
                            )}
                          </strong>
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  )}

                  <div className="m-4 justify-content-end">
                    {signature?.isActive && (
                      <div className="signatureBtnWrapper">
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>Signature</strong>
                        </div>
                        <div
                          style={{
                            minHeight: signature?.image ? "0px" : "120px",
                            display: signature?.image ? "block" : "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {signature?.image && (
                            <>
                              <div className="signatueImgWrapper border">
                                <img
                                  src={signature?.image}
                                  alt="Signed"
                                  style={{ width: "160px" }}
                                />
                              </div>

                              <div>
                                <div className="form-group">
                                  <label
                                    className="form-label"
                                    htmlFor="signatureLabel"
                                  >
                                    Signature Label
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    name="signatureLabel"
                                    onChange={(e) => {}}
                                    disabled
                                    defaultValue={
                                      signature?.label ||
                                      "Authorised Signatory"
                                    }
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Panel>
              <Panel header="Other Information" key="3">
                <Descriptions title="" className="custom-description-title">
                  <Descriptions.Item label="Instructions">
                    <span
                      style={{
                        color: "#2263b3",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => setInstructionsVisible(true)}
                    >
                      View
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Terms and Conditions">
                    <span
                      style={{
                        color: "#2263b3",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => setTermsVisible(true)}
                    >
                      View
                    </span>
                  </Descriptions.Item>
                  {attachments?.length > 0 && (
                    <Descriptions.Item label="">
                      <div>
                        <div className="">
                          Attachments &nbsp;
                          <PaperClipOutlined />
                        </div>
                        <div>
                          {attachments?.map(
                            (attachment: any, index: number) => (
                              <div
                                key={index}
                                className="w-100 d-flex justify-content-between"
                              >
                                <div>
                                  <strong>{index + 1}.</strong>
                                  &nbsp;
                                  {attachment.name}
                                  &nbsp; &nbsp; &nbsp; &nbsp;
                                </div>
                                <div>
                                  <a
                                    href={attachment.link}
                                    download={attachment.name}
                                    style={{ color: "black", fontSize: "18px" }}
                                  >
                                    <DownloadOutlined />
                                  </a>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Panel>
            </AntCollapse>
          </>
        ) : (
          <>
            <Flex align="center" gap="middle" className="pageLoader">
              <Spin size="large" className="m-auto" />
            </Flex>
          </>
        )}
        <Modal
          title={undefined}
          open={instructionsVisible}
          footer={null}
          onCancel={() => {
            setInstructionsVisible(false);
          }}
          width="60%"
          bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <div className="d-flex">
            <h5 className="m-1">Instructions </h5>
          </div>
          {instructions ? (
            <div
              dangerouslySetInnerHTML={{
                __html: instructions || "",
              }}
              className="m-1"
            />
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Empty description="No Instructions Available for this Credit Note" />
            </div>
          )}
        </Modal>
        <Modal
          title={undefined}
          open={termsVisible}
          footer={null}
          onCancel={() => {
            setTermsVisible(false);
          }}
          width="60%"
          bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          <div className="d-flex">
            <h5 className="m-1">Terms and Conditions</h5>
          </div>
          {termsAndConditions ? (
            <div
              dangerouslySetInnerHTML={{
                __html: termsAndConditions || "",
              }}
              className="m-1"
            />
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Empty description="No Terms and Conditions Available for this Credit Note" />
            </div>
          )}
        </Modal>
      </Drawer>
    </Drawer>
  );
};

export default AccountsPayablesDrawer;

